import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { CarCard } from './CarCard';
import { vehicles } from '../data/vehicles';
function parsePrice(price: string): number {
  return Number(price.replace(/[$.]/g, ''));
}
type SortOrder = 'none' | 'asc' | 'desc';
interface CarListingGridProps {
  searchQuery?: string;
  selectedType?: string | null;
}
export function CarListingGrid({
  searchQuery = '',
  selectedType
}: CarListingGridProps) {
  const [sortOpen, setSortOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>('none');
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const filteredCars = vehicles.filter(car => {
    if (selectedType && car.type !== selectedType) return false;
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const searchString = `${car.year} ${car.make} ${car.model} ${car.trim}`.toLowerCase();
    return searchString.includes(query);
  });
  const sortedCars = [...filteredCars].sort((a, b) => {
    // Vendidos siempre al final del listado
    if (Boolean(a.sold) !== Boolean(b.sold)) return a.sold ? 1 : -1;
    if (sortOrder === 'desc') return parsePrice(b.price) - parsePrice(a.price);
    if (sortOrder === 'asc') return parsePrice(a.price) - parsePrice(b.price);
    return 0;
  });
  const sortLabel = sortOrder === 'desc' ? 'Mayor precio' : sortOrder === 'asc' ? 'Menor precio' : 'Ordenar';
  return <div className="w-full" data-id="element-27" data-mp="18zn08ltcr1k0">
      {/* Results Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4" data-id="element-28" data-mp="1dygissnhxir1">
        <span className="text-[15px] text-[#C6C4B9]" data-id="element-29" data-mp="15xs9s98jhin0">
          {filteredCars.length}{' '}
          {filteredCars.length === 1 ? 'resultado' : 'resultados'}
          {searchQuery.trim() && ` para "${searchQuery.trim()}"`}
        </span>

        <div className="flex items-center gap-6" data-id="element-30" data-mp="1fnk9tizer3yv">
          <div className="relative" ref={dropdownRef} data-id="element-31" data-mp="1apaen8doo3d9">
            <button onClick={() => setSortOpen(!sortOpen)} className="flex items-center gap-2 text-[15px] font-medium text-[#F2F0E9] hover:text-[#C9A24E] transition-colors" data-id="element-32" data-mp="1apsgckj479sk">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-33" data-mp="i2plq3hk7j7l">
                <line x1="21" y1="10" x2="3" y2="10" data-id="element-34" data-mp="28axyktsyct7w"></line>
                <line x1="21" y1="6" x2="3" y2="6" data-id="element-35" data-mp="23eh7prudc0r4"></line>
                <line x1="21" y1="14" x2="3" y2="14" data-id="element-36" data-mp="23g0p5lrrghuh"></line>
                <line x1="21" y1="18" x2="3" y2="18" data-id="element-37" data-mp="2awfh7wcjvbuc"></line>
              </svg>
              {sortLabel}
              <ChevronDown className={`w-4 h-4 text-[#A8AA9E] transition-transform duration-200 ${sortOpen ? 'rotate-180' : ''}`} data-id="element-38" data-mp="2fxv1hm6wlebo" />
            </button>

            {sortOpen && <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 bg-[#2B2E27] border-2 border-[#C9A24E] rounded-lg shadow-lg py-1 z-50 min-w-[160px]" data-id="element-39" data-mp="25ydw75vkd2g8">
                <button onClick={() => {
              setSortOrder('desc');
              setSortOpen(false);
            }} className={`w-full text-left px-4 py-2.5 text-[14px] transition-colors ${sortOrder === 'desc' ? 'text-[#C9A24E] font-semibold bg-[#343830]' : 'text-[#C6C4B9] hover:bg-[#343830] hover:text-[#C9A24E]'}`} data-id="element-40" data-mp="2ylq9epfu3xwv">
                  Mayor precio
                </button>
                <button onClick={() => {
              setSortOrder('asc');
              setSortOpen(false);
            }} className={`w-full text-left px-4 py-2.5 text-[14px] transition-colors ${sortOrder === 'asc' ? 'text-[#C9A24E] font-semibold bg-[#343830]' : 'text-[#C6C4B9] hover:bg-[#343830] hover:text-[#C9A24E]'}`} data-id="element-41" data-mp="1toxzao0zx5gu">
                  Menor precio
                </button>
              </div>}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-42" data-mp="27hdwv9b4obad">
        {sortedCars.length > 0 ? sortedCars.map(car => <CarCard key={car.id} {...car} data-id="element-43" data-mp="22njce3np7sa8" />) : <div className="col-span-full text-center py-16" data-id="element-44" data-mp="1ymzjjca5ratw">
            <p className="text-lg text-[#F2F0E9]" data-id="element-45" data-mp="2v5x3zlifyi0v">
              No se encontraron autos para "{searchQuery}"
            </p>
            <p className="text-sm text-[#A8AA9E] mt-2" data-id="element-46" data-mp="2qeyjsauc0zsc">
              Intentá con otra búsqueda
            </p>
          </div>}
      </div>
    </div>;
}